import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  updateDisplayBigNumbersOffersPrimary,
  updateDisplayPrimaryCharts,
  updateRealtyOffersPrimary,
} from '../../redux/offersPrimary.actions';

import {
  setDisplayCharts,
  updateClusters,
  updateMarkers,
} from '../../../redux/dashboardActions';

import {
  validateForm,
  payloadBuilder,
} from '../../redux/offersPrimary.controller';

import { NameIdPair } from '../../../types';
import { Button, SelectFiltered } from '../../../../../component';
import { resetFilters } from '../../../charts/redux/charts.actions';

interface BuildingFiltersProps {
  closeModalAction: () => void;
}

export const BuildingFiltersMenu = ({
  closeModalAction,
}: BuildingFiltersProps) => {
  const companiesStorage = localStorage.getItem('Companies') as string;
  const buildingsStorage = localStorage.getItem('BuildingsNames') as string;
  const companiesDefault = JSON.parse(companiesStorage);
  const buildingsDefault = JSON.parse(buildingsStorage);

  const dispatch = useDispatch();

  const { moreFilters } = useSelector((state: any) => state.moreFiltersState);

  const { realtyOffersFiltersOptions, realtyOffersPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );

  const { markers, clusters } = useSelector(
    (state: any) => state.dashboardState
  );

  const { buildings, companies } = realtyOffersFiltersOptions;

  const { selectedBuildings, selectedCompanies } = realtyOffersPrimary;

  const [companiesOptions, setCompaniesOptions] = useState<NameIdPair[]>(
    companiesDefault || null
  );
  const [buildingsOptions, setBuildingsOptions] = useState<NameIdPair[]>(
    buildingsDefault || null
  );

  const [hasUpdate, setHasUpdate] = useState<boolean>(false);
  const [isBuildingFilter, setIsBuildingFilter] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (hasUpdate && isBuildingFilter) {
      if (isBuildingFilter) {
        dispatch(
          payloadBuilder(
            realtyOffersPrimary,
            null,
            'primary',
            true,
            true,
            'building'
          )
        );
      }
    } else {
      dispatch(
        payloadBuilder(realtyOffersPrimary, null, 'primary', true, true)
      );
    }

    setHasUpdate(false);
    setIsBuildingFilter(false);
    setTimeout(() => setIsLoading(false), 2000);
  }, [hasUpdate, isBuildingFilter]);

  useEffect(() => {
    setBuildingsOptions(buildings);
  }, [buildings]);

  useEffect(() => {
    setCompaniesOptions(companies);
  }, [companies]);

  const handleClickMultiSelectFilters = async (e: any, arrayName: string) => {
    const formattedValues = e?.value?.map((filter: any) => filter.name);

    const value = e?.value;
    const filterType = e?.name;

    const isBuilding = filterType === 'building';
    const BUILDING_NAMES_KEY = 'buildingNames';
    const REAL_STATE_DEVELOPERS_KEY = 'realStateDevelopers';

    const payload = {
      name: arrayName,
      value: value?.filter(
        (el: { name: string; id: string; parent: boolean }) => el !== undefined
      ),
    };

    dispatch([
      updateRealtyOffersPrimary(payload),
      updateRealtyOffersPrimary({
        name: isBuilding ? BUILDING_NAMES_KEY : REAL_STATE_DEVELOPERS_KEY,
        value: formattedValues,
      }),
    ]);

    setIsLoading(true);
    setIsBuildingFilter(!!isBuilding);
    setHasUpdate(true);
  };

  const handleFilterUpdate = (name: any, userInput: any) => {
    if (name === 'building' && userInput.length >= 4) {
      const filtered = buildings.filter((el: NameIdPair) =>
        el.name.toLowerCase().includes(userInput.toLowerCase())
      );
      setBuildingsOptions(filtered);
    } else if (name === 'building' && userInput.length === 0) {
      setBuildingsOptions(buildings);
    } else if (name === 'company' && userInput.length >= 4) {
      const filtered = companies.filter((el: any) =>
        el.name.toLowerCase().includes(userInput.toLowerCase())
      );
      setCompaniesOptions(filtered);
    } else if (name === 'company' && userInput.length === 0) {
      setCompaniesOptions(companies);
    }
  };

  const resetOptions = () => {
    setBuildingsOptions(buildingsDefault);
    setCompaniesOptions(companiesDefault);
    setIsLoading(false);
  };

  const handleSubmit = () => {
    if (clusters.activeMap_ !== undefined) {
      markers.forEach((el: any) => {
        el.setMap(null);
      });
      clusters.clearMarkers();
      dispatch([updateMarkers([]), updateClusters([])]);
    }
    dispatch([
      setDisplayCharts(null),
      validateForm(realtyOffersPrimary, {}, 'primary', moreFilters),
      closeModalAction?.(),
    ]);
  };

  const handleClearFilters = () => {
    dispatch([
      updateRealtyOffersPrimary({ name: 'realStateDevelopers', value: [] }),
      updateRealtyOffersPrimary({ name: 'buildingNames', value: [] }),
      updateRealtyOffersPrimary({ name: 'selectedBuildings', value: [] }),
      updateRealtyOffersPrimary({ name: 'selectedCompanies', value: [] }),
      validateForm(realtyOffersPrimary, {}, 'primary', moreFilters, true),
      resetFilters(),
    ]);
    setIsLoading(true);
    setHasUpdate(true);
    setIsBuildingFilter(true);
    resetOptions();
  };

  return (
    <div className="buildings-menu" data-cy="BuildingsFilter">
      <section className="buildings-menu__content">
        <section className="buildings-menu__content__section">
          <section className="buildings-menu__content__section__title">
            <p>Incorporadoras</p>
          </section>
          <section className="buildings-menu__content__section__filters">
            <div className="input-section-container">
              {companies && companies?.length > 0 ? (
                <SelectFiltered
                  style={{ width: '100%' }}
                  cy="CompaniesFilter"
                  label=""
                  options={companiesOptions}
                  multiSelect
                  name="company"
                  action={(e: any) =>
                    handleClickMultiSelectFilters(e, 'selectedCompanies')
                  }
                  actionFilter={(e, v) => handleFilterUpdate('company', v)}
                  selected={selectedCompanies || ''}
                  filter
                />
              ) : (
                <p>
                  Sem resultados de incorporadoras para o filtro selecionado.
                </p>
              )}
            </div>
          </section>
        </section>
        <section className="buildings-menu-wrapper__content__section">
          <section className="buildings-menu__content__section__title">
            <p>Empreendimentos</p>
          </section>
          <section className="buildings-menu__content__section__filters">
            <div className="input-section-container">
              {buildings && buildings?.length > 0 ? (
                <SelectFiltered
                  style={{ width: '100%' }}
                  cy="BuildingsFilter"
                  name="building"
                  label=""
                  options={buildingsOptions}
                  multiSelect
                  action={(e: any) =>
                    handleClickMultiSelectFilters(e, 'selectedBuildings')
                  }
                  actionFilter={(e, v) => handleFilterUpdate('building', v)}
                  selected={selectedBuildings || ''}
                  filter
                />
              ) : (
                <p>
                  Sem resultados de empreendimentos para o filtro selecionado.
                </p>
              )}
            </div>
          </section>
        </section>
      </section>
      <section className="buildings-menu__buttons">
        <Button
          cy="ClearBuildingsFilter"
          variant="outline"
          size="block"
          onClick={handleClearFilters}
          className="btn secondary outline block"
        >
          Limpar filtro
        </Button>
        <Button
          cy="ApplyBuildingsFilter"
          color="primary"
          size="block"
          onClick={handleSubmit}
          className="btn primary normal block apply-button"
        >
          Buscar no mapa
        </Button>
      </section>
    </div>
  );
};
